import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Horizontal-bar comparison chart showing each BD's total jobs vs converted
 * jobs for the selected period. Accepts the enriched bd_statistics payload
 * (falls back gracefully if only job_count is present).
 */
const BDPerformanceChart = ({ bdStats }) => {
  if (!bdStats || bdStats.length === 0) {
    return (
      <div className="insights-chart-empty">
        <div className="empty-state">
          <div className="empty-state-title">No BD data for this period</div>
          <div className="empty-state-desc">
            Try selecting a wider date range to see activity.
          </div>
        </div>
      </div>
    );
  }

  // Order: largest at the top
  const sorted = [...bdStats].sort((a, b) => (b.job_count || 0) - (a.job_count || 0));

  const labels = sorted.map((s) => s.bd_name);
  const jobCounts = sorted.map((s) => s.job_count || 0);
  const conversions = sorted.map((s) => s.converted_count || 0);

  const data = {
    labels,
    datasets: [
      {
        label: 'Jobs',
        data: jobCounts,
        backgroundColor: 'rgba(16, 185, 129, 0.85)',
        borderRadius: 6,
        borderSkipped: false,
        barPercentage: 0.7,
        categoryPercentage: 0.75,
      },
      {
        label: 'Completed',
        data: conversions,
        backgroundColor: 'rgba(59, 130, 246, 0.85)',
        borderRadius: 6,
        borderSkipped: false,
        barPercentage: 0.7,
        categoryPercentage: 0.75,
      },
    ],
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 14,
          color: '#374151',
          font: { size: 12, weight: '500', family: 'Inter, sans-serif' },
          usePointStyle: true,
          pointStyle: 'rectRounded',
        },
      },
      tooltip: {
        backgroundColor: '#111827',
        titleColor: '#fff',
        bodyColor: '#e5e7eb',
        padding: 12,
        cornerRadius: 8,
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 12 },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: '#f3f4f6', drawBorder: false },
        ticks: {
          color: '#6b7280',
          stepSize: 1,
          font: { size: 11, family: 'Inter, sans-serif' },
        },
      },
      y: {
        grid: { display: false, drawBorder: false },
        ticks: {
          color: '#374151',
          font: { size: 12, weight: '500', family: 'Inter, sans-serif' },
        },
      },
    },
  };

  // Scale chart height to number of BDs for comfortable row spacing
  const height = Math.max(240, labels.length * 44 + 60);

  return (
    <div className="insights-chart-wrapper" style={{ height: `${height}px` }}>
      <Bar data={data} options={options} />
    </div>
  );
};

export default BDPerformanceChart;
